/**
 * mcpbSync.js — pure predicates for the MCPlayback multi-cam buffer-gate (Phase 2a-3).
 *
 * Mirrors the Java MCBufferGate model: the shared master clock may be HELD (paused for a beat) while a
 * genuinely-playing cell underruns mid-playback, so the grid stays in lockstep instead of one tile
 * running ahead while a slow tile rebuffers. Crucially this is a 5-second STALL CEILING, NOT a
 * "pre-buffer N seconds before playing" — a gap/empty/dead/never-rendered cell must NEVER freeze the
 * grid, and even a genuine underrun releases the clock after the ceiling so one wedged stream can't
 * hang playback forever. The ceiling (how long the clock has held) is tracked by useTimelinePlayhead;
 * these predicates only answer "does any cell WANT to stall right now".
 */

/** Default stall ceiling in seconds (Java MCBufferGate.DEFAULT_MAX_STALL_SECONDS). */
export const DEFAULT_MAX_STALL_SEC = 5;

/**
 * Can this slot stall the shared clock? Only a cell that has a clip under the playhead AND is currently
 * buffering counts. Gap cells (no clip) and idle cells never stall — they are expected to be dark, so
 * they must not hold the playing tiles back.
 * @param {{hasClip:boolean, isBuffering:boolean}} s
 */
export const slotCanStall = (s) => !!(s && s.hasClip && s.isBuffering);

/** True iff at least one cell genuinely stalls (the master should consider holding this tick). */
export const anyCellCanStall = (slotStates) => (slotStates || []).some(slotCanStall);

/**
 * Did this cell just finish loading? True only when a cell that WAS genuinely buffering reports it is
 * ready (or has moved into a gap). The View re-checks the gate right then, so the grid starts the moment
 * the last held cell is ready instead of on the next 1 Hz tick (86bc8571y). A cell that never buffered
 * does not count: a stray ready event from it must not open the gate before a slower sibling has
 * reported its own reload.
 * @param {{hasClip:boolean, isBuffering:boolean}|undefined} prev  the cell's state before this report
 * @param {{hasClip?:boolean, isBuffering?:boolean}} partial        the report being merged in
 */
export const cellBecameReady = (prev, partial) => !!(slotCanStall(prev) && partial && partial.isBuffering === false
  // A finished cloud cache pull is not "ready": that tile has no src yet and reports its own load next
  // (a <video> only on its async loadstart), so the tick decides the gate there.
  && !('caching' in partial));

/**
 * Progress-aware hold aggregation (ADR-030). Given each genuinely-buffering ("held") cell's progress
 * this tick — `{ caching, grew }` (caching = a cloud clip being pulled to cache; grew = its <video>
 * buffer end advanced) — decide whether the grid should hold INDEFINITELY past the stall ceiling.
 *
 * Hold indefinitely ONLY while EVERY held cell is still loading. If ANY held cell is wedged (not caching
 * AND its buffer didn't grow), return false so the 5s ceiling accrues and the grid recovers — a single
 * caching sibling must NOT mask a dead cell and freeze the whole grid until the cache timeout (~30s).
 * (Fixes the prior any-cell-progressing short-circuit: one caching cell zeroed the wedge ceiling for all.)
 * @param {Array<{caching?:boolean, grew?:boolean}>} heldCells
 */
export const heldCellsAllProgressing = (heldCells) => {
  const held = heldCells || [];
  if (!held.length) return false; // nothing held ⇒ nothing to hold for
  return held.every((c) => !!(c && (c.caching || c.grew)));
};
